/**
 * Overview — the staff dashboard: one snapshot (GET /admin/overview) rendered
 * as stat tiles + 14-day trends + a needs-attention strip that deep-links
 * into the working sections. Dense and factual; the announcer voice stays
 * out of the ops room.
 */
import type { ReactNode } from "react";
import { copy } from "@humble/shared";
import { Button, Card, Skeleton } from "../../design/index.js";
import { MeterRow, SparkBars, compact, type SeriesPoint } from "./charts.js";
import type { Loaded } from "./use-load.js";

const C = copy.admin.overview;

export interface Overview {
  generated_at: string;
  users: {
    total: number;
    by_lifecycle: Record<string, number>;
    by_gender: Record<string, number>;
    verified: number;
    new_today: number;
    new_7d: number;
    signup_series: SeriesPoint[];
  };
  activity: { online_now: number; active_today: number; active_7d: number };
  matching: {
    total_matches: number;
    open_now: number;
    classic: number;
    reject: number;
    roulette: number;
    matches_today: number;
    matches_7d: number;
    super_rejects_total: number;
    super_rejects_7d: number;
    avg_matches_per_active_user: number;
    match_series: SeriesPoint[];
  };
  moderation: {
    reports_open: number;
    reports_in_review: number;
    reports_resolved_7d: number;
    reports_sla_breached: number;
    media_scanning: number;
    media_quarantined: number;
    verification_pending: number;
  };
  monetization: {
    subscriptions_active: number;
    subscriptions_by_status: Record<string, number>;
    purchases_total: number;
    revenue_cents_net: number;
  };
}

function StatTile({
  label,
  value,
  sub,
  delta,
  trend,
  testid,
}: {
  label: string;
  value: string;
  sub?: string;
  delta?: string;
  trend?: { series: SeriesPoint[]; title: string };
  testid?: string;
}) {
  return (
    <Card padding="md" className="admin__tile" data-testid={testid}>
      <p className="admin__tile-label">{label}</p>
      <p className="admin__tile-value">{value}</p>
      {(delta || sub) && (
        <p className="admin__tile-sub">
          {delta && <span className="admin__tile-delta">{delta}</span>}
          {delta && sub ? " · " : ""}
          {sub}
        </p>
      )}
      {trend && <SparkBars series={trend.series} title={trend.title} />}
    </Card>
  );
}

function AttentionCard({
  label,
  count,
  severity,
  note,
  action,
  onOpen,
}: {
  label: string;
  count: number;
  severity: "serious" | "warning";
  note?: string;
  action: string;
  onOpen: () => void;
}) {
  return (
    <Card
      padding="md"
      className={`admin__attention-card${count > 0 ? ` admin__attention-card--${severity}` : ""}`}
    >
      <div className="admin__attention-body">
        <p className="admin__tile-value">{compact(count)}</p>
        <div>
          <p className="admin__tile-label">{label}</p>
          {note && <p className="admin__tile-sub">{note}</p>}
        </div>
      </div>
      <Button size="sm" variant={count > 0 ? "primary" : "ghost"} onClick={onOpen}>
        {action}
      </Button>
    </Card>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card padding="md" className="admin__panel">
      <h3 className="admin__panel-title">{title}</h3>
      {children}
    </Card>
  );
}

/** Fixed display order — lifecycle is a pipeline, not an alphabet. */
const LIFECYCLE_ORDER = [
  "active",
  "onboarding",
  "registered_unverified",
  "paused",
  "deletion_grace",
  "banned",
  "purged",
];

export function OverviewSection({
  overview,
  onNavigate,
}: {
  overview: Loaded<Overview>;
  onNavigate: (section: "moderation" | "verification") => void;
}) {
  const { data, loading, error, reload } = overview;

  if (error) {
    return (
      <Card padding="lg" className="admin__error">
        <p>{C.loading_failed}</p>
        <Button size="sm" variant="primary" onClick={reload}>
          Retry
        </Button>
      </Card>
    );
  }
  if (loading || !data) {
    return (
      <div className="admin__tiles" aria-busy="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={112} radius={18} />
        ))}
      </div>
    );
  }

  const m = data.moderation;
  const mediaInReview = m.media_scanning + m.media_quarantined;
  const allClear = m.reports_open === 0 && m.verification_pending === 0 && mediaInReview === 0;
  const revenue = `$${(data.monetization.revenue_cents_net / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  const lifecycleMax = Math.max(1, ...Object.values(data.users.by_lifecycle));
  const genderMax = Math.max(1, ...Object.values(data.users.by_gender));
  const matchMax = Math.max(1, data.matching.classic, data.matching.reject, data.matching.roulette);

  return (
    <div className="admin__overview" data-testid="admin-overview">
      <section className="admin__attention" data-testid="admin-attention" aria-label={C.needs_attention}>
        <h3 className="admin__panel-title">{C.needs_attention}</h3>
        {allClear && <p className="admin__empty admin__empty--left">{C.all_clear}</p>}
        <div className="admin__attention-grid">
          <AttentionCard
            label={C.open_reports}
            count={m.reports_open}
            severity="serious"
            note={
              m.reports_sla_breached > 0
                ? `${m.reports_sla_breached} ${C.sla_breached} · ${m.reports_in_review} in review`
                : `${m.reports_in_review} in review · ${m.reports_resolved_7d} resolved 7d`
            }
            action={copy.admin.nav.moderation}
            onOpen={() => onNavigate("moderation")}
          />
          <AttentionCard
            label={C.verification_queue}
            count={m.verification_pending}
            severity="warning"
            action={copy.admin.nav.verification}
            onOpen={() => onNavigate("verification")}
          />
          <AttentionCard
            label={C.media_review}
            count={mediaInReview}
            severity="warning"
            note={`${m.media_quarantined} quarantined · ${m.media_scanning} scanning`}
            action={copy.admin.nav.moderation}
            onOpen={() => onNavigate("moderation")}
          />
        </div>
      </section>

      <div className="admin__tiles">
        <StatTile
          testid="stat-users-total"
          label={C.users_total}
          value={compact(data.users.total)}
          delta={`+${compact(data.users.new_7d)} ${C.vs_prev_7d}`}
          sub={`${compact(data.users.new_today)} today`}
          trend={{ series: data.users.signup_series, title: `${C.new_signups} — ${C.last_14d}` }}
        />
        <StatTile
          testid="stat-active-today"
          label={C.active_today}
          value={compact(data.activity.active_today)}
          sub={`${compact(data.activity.online_now)} ${C.online_now} · ${compact(data.activity.active_7d)} in 7d`}
        />
        <StatTile
          testid="stat-matches"
          label={C.matches}
          value={compact(data.matching.total_matches)}
          delta={`+${compact(data.matching.matches_7d)} ${C.vs_prev_7d}`}
          sub={`${compact(data.matching.open_now)} open · ${data.matching.avg_matches_per_active_user}/active user`}
          trend={{ series: data.matching.match_series, title: `${C.matches} — ${C.last_14d}` }}
        />
        <StatTile
          testid="stat-verified"
          label={C.verified_users}
          value={compact(data.users.verified)}
          sub={`${compact(m.verification_pending)} pending review`}
        />
        <StatTile
          testid="stat-subscriptions"
          label={C.subscriptions}
          value={compact(data.monetization.subscriptions_active)}
          sub={Object.entries(data.monetization.subscriptions_by_status)
            .filter(([s]) => s !== "active")
            .map(([s, n]) => `${n} ${s}`)
            .join(" · ")}
        />
        <StatTile
          testid="stat-revenue"
          label={C.revenue}
          value={revenue}
          sub={`${compact(data.monetization.purchases_total)} purchases`}
        />
      </div>

      <div className="admin__panels">
        <Panel title={C.by_lifecycle}>
          {LIFECYCLE_ORDER.filter((k) => k in data.users.by_lifecycle).map((k) => (
            <MeterRow key={k} label={k} value={data.users.by_lifecycle[k] ?? 0} max={lifecycleMax} />
          ))}
        </Panel>
        <Panel title={C.by_gender}>
          {Object.entries(data.users.by_gender)
            .sort(([, a], [, b]) => b - a)
            .map(([k, v]) => (
              <MeterRow key={k} label={k} value={v} max={genderMax} />
            ))}
        </Panel>
        <Panel title={C.match_split}>
          <MeterRow label="classic" value={data.matching.classic} max={matchMax} />
          <MeterRow label="reject" value={data.matching.reject} max={matchMax} />
          <MeterRow label="roulette" value={data.matching.roulette} max={matchMax} />
          <p className="admin__panel-foot">
            {compact(data.matching.super_rejects_total)} super-rejects total ·{" "}
            {compact(data.matching.super_rejects_7d)} in 7d
          </p>
        </Panel>
      </div>

      <p className="admin__generated">
        {new Date(data.generated_at).toLocaleString()}{" "}
        <Button size="sm" variant="ghost" onClick={reload}>
          Refresh
        </Button>
      </p>
    </div>
  );
}
